import { getMapboxAccessToken, MAPBOX_API } from '../constants';

/**
 * Reverse-geocode a coordinate to a human-readable place name via the Mapbox
 * Geocoding API.
 *
 * Used to label manually-added pins. The returned label is UI-only and never
 * sent to the backend; callers fall back to a formatted "lat, lng" string when
 * this resolves to `null`.
 *
 * @see https://docs.mapbox.com/api/search/geocoding-v5/#reverse-geocoding
 *
 * @param longitude - Longitude of the point to resolve.
 * @param latitude - Latitude of the point to resolve.
 * @param accessToken - Optional Mapbox token; defaults to the env-derived token.
 * @returns The first feature's `place_name`, or `null` on any error / empty result.
 *
 * @remarks Never throws — every failure path resolves to `null`.
 *
 * @example
 * ```typescript
 * const label = await fetchReverseGeocode(2.3522, 48.8566);
 * // => 'Paris, France' | null
 * ```
 */
export async function fetchReverseGeocode(
  longitude: number,
  latitude: number,
  accessToken?: string,
): Promise<string | null> {
  return reverseGeocode(longitude, latitude, accessToken);
}

/** The nearest street. Null when the point has none, or the lookup fails. */
export async function fetchStreetAddress(
  longitude: number,
  latitude: number,
): Promise<string | null> {
  return reverseGeocode(longitude, latitude, undefined, 'address');
}

async function reverseGeocode(
  longitude: number,
  latitude: number,
  accessToken?: string,
  types?: string,
): Promise<string | null> {
  try {
    // Resolve the token INSIDE the try: `getMapboxAccessToken()` throws when the
    // env var is missing, and a default-parameter call would escape this catch,
    // breaking the never-throws contract (matches directions.service.ts).
    const token = accessToken ?? getMapboxAccessToken();

    const params = new URLSearchParams();
    params.set('access_token', token);
    params.set('limit', '1');
    if (types) params.set('types', types);

    const url = `${MAPBOX_API.GEOCODING}/${longitude},${latitude}.json?${params.toString()}`;

    const res = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!res.ok) {
      return null;
    }

    const json: unknown = await res.json();
    const placeName = extractPlaceName(json);
    return placeName;
  } catch {
    return null;
  }
}

/**
 * Safely extract `features[0].place_name` from an untrusted Mapbox response.
 *
 * @param json - The parsed JSON response.
 * @returns The place name string, or `null` if the shape does not match.
 */
function extractPlaceName(json: unknown): string | null {
  if (typeof json !== 'object' || json === null || !('features' in json)) {
    return null;
  }

  const features = (json as { features: unknown }).features;
  if (!Array.isArray(features) || features.length === 0) {
    return null;
  }

  const first: unknown = features[0];
  if (typeof first !== 'object' || first === null || !('place_name' in first)) {
    return null;
  }

  const placeName = (first as { place_name: unknown }).place_name;
  return typeof placeName === 'string' ? placeName : null;
}
